import React from "react";
import { MoreVertical, Edit, Trash2 } from "lucide-react";
import { State } from "../../../api/services/state/type";

interface StateTableProps {
  states: State[];
  onEdit?: (state: State) => void;
  onDelete?: (stateId: string) => void;
  isLoading?: boolean;
}

export const StateTable: React.FC<StateTableProps> = ({
  states,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const handleMenuToggle = (stateId: string) => {
    setOpenMenuId(openMenuId === stateId ? null : stateId);
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading states...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-visible">
      <table className="w-full">
        <thead className="bg-slate-700">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              State Name
            </th>
            <th className="px-6 py-4 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700">
          {states.map((state) => (
            <tr key={state.id} className="hover:bg-slate-700 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {state.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-white">
                      {state.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="relative inline-block">
                  <button
                    onClick={() => handleMenuToggle(state.id)}
                    className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-600 transition-colors"
                  >
                    <MoreVertical size={16} />
                  </button>

                  {openMenuId === state.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-md shadow-lg z-10 border border-slate-600">
                      <div className="py-1">
                        {onEdit && (
                          <button
                            onClick={() => handleAction(() => onEdit(state))}
                            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white transition-colors"
                          >
                            <Edit size={16} className="mr-3" />
                            Edit State
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() =>
                              handleAction(() => onDelete(state.id))
                            }
                            className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                          >
                            <Trash2 size={16} className="mr-3" />
                            Delete State
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {states.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No states found</div>
        </div>
      )}

      {openMenuId && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
